/**
 * Deletion Warning Context
 * Global state for account deletion warning banner
 * Tracks pending/scheduled account deletion and allows cancellation
 * Uses React Query for server state (deletion status).
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react'

import { useTranslation } from '@platform/i18n/hooks/useTranslation'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import {
  getDeletionStatus,
  cancelDeletionRequest,
  type DeletionRequest,
} from '@/platform/profile/services/gdpr.service'
import { useNotifications } from '@/shared/components/Notification'
import { logger } from '@/shared/utils/logger'

import { useAuth } from './AuthContext'

// ─────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────

interface DeletionWarningContextValue {
  /** Current deletion request, if any */
  deletionRequest: DeletionRequest | null
  /** Whether we're loading the deletion status */
  isLoading: boolean
  /** Whether a cancellation is in progress */
  isCancelling: boolean
  /** Days remaining until deletion */
  daysRemaining: number | null
  /** Cancel the deletion request */
  cancelDeletion: () => Promise<boolean>
  /** Refresh deletion status */
  refresh: () => Promise<void>
  /** Dismiss the banner temporarily (session only) */
  dismissTemporarily: () => void
  /** Whether banner is temporarily dismissed */
  isDismissed: boolean
}

// ─────────────────────────────────────────────────────────────
// CONTEXT
// ─────────────────────────────────────────────────────────────

const DeletionWarningContext = createContext<DeletionWarningContextValue | undefined>(undefined)

// ─────────────────────────────────────────────────────────────
// QUERY KEYS
// ─────────────────────────────────────────────────────────────

export const deletionWarningKeys = {
  status: ['deletion-warning', 'status'] as const,
}

/**
 * The account's ACTIVE deletion request, or `null`.
 *
 * "Active" is `pending` (what the server reports while the grace period runs) or `scheduled`.
 * The banner and the Privacy tab read THIS through the same React Query key
 * (`deletionWarningKeys.status`), so they cannot disagree: the tab used to keep its own local
 * copy, filled only when a deletion was requested in that very session, and compared it with
 * `'scheduled'` — after a reload the request was `pending` on the server and the tab showed
 * nothing to cancel.
 */
export async function fetchActiveDeletionRequest(): Promise<DeletionRequest | null> {
  try {
    const status = await getDeletionStatus()
    if (status && (status.status === 'pending' || status.status === 'scheduled')) {
      return status
    }
    return null
  } catch {
    return null
  }
}

// ─────────────────────────────────────────────────────────────
// PROVIDER
// ─────────────────────────────────────────────────────────────

export const DeletionWarningProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth()
  const queryClient = useQueryClient()
  const { t } = useTranslation('shared')
  const { success: showSuccess, error: showError } = useNotifications()
  const [isDismissed, setIsDismissed] = useState(false)

  // Fetch deletion status via React Query
  const {
    data: deletionRequest,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: deletionWarningKeys.status,
    queryFn: async (): Promise<DeletionRequest | null> => {
      if (!isAuthenticated) return null
      return fetchActiveDeletionRequest()
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes (replaces previous polling interval)
    refetchInterval: isAuthenticated ? 5 * 60 * 1000 : false, // poll every 5 minutes
    gcTime: 10 * 60 * 1000,
  })

  // Calculate days remaining until deletion
  const daysRemaining = useMemo(() => {
    if (!deletionRequest?.scheduledFor) return null
    const scheduledDate = new Date(deletionRequest.scheduledFor)
    const now = new Date()
    const diffMs = scheduledDate.getTime() - now.getTime()
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))
    return Math.max(0, diffDays)
  }, [deletionRequest])

  // Cancel deletion mutation
  const cancelMutation = useMutation({
    mutationFn: (id: string) => cancelDeletionRequest(id),
    onSuccess: () => {
      // Invalidate so status re-fetches and becomes null → the banner clears.
      queryClient.invalidateQueries({ queryKey: deletionWarningKeys.status })
      // Confirm to the user their account will NOT be deleted.
      showSuccess(t('deletionWarning.cancelSuccess'))
    },
    onError: (error) => {
      // NEVER swallow a failed cancel: the account is STILL scheduled for
      // deletion, so the user must know the cancel did not go through —
      // otherwise they believe erasure is stopped when it isn't (data loss).
      logger.error('Failed to cancel deletion:', error)
      showError(t('deletionWarning.cancelError'))
    },
  })

  // Cancel deletion request
  const cancelDeletion = useCallback(async (): Promise<boolean> => {
    if (!deletionRequest) return false
    try {
      await cancelMutation.mutateAsync(deletionRequest.id)
      return true
    } catch {
      // The failure is surfaced (toast + log) by the mutation's onError handler.
      return false
    }
  }, [deletionRequest, cancelMutation])

  // Dismiss temporarily (session only)
  const dismissTemporarily = useCallback(() => {
    setIsDismissed(true)
  }, [])

  // Manual refresh
  const refresh = useCallback(async () => {
    await refetch()
  }, [refetch])

  const value = useMemo(
    () => ({
      deletionRequest: deletionRequest ?? null,
      isLoading,
      isCancelling: cancelMutation.isPending,
      daysRemaining,
      cancelDeletion,
      refresh,
      dismissTemporarily,
      isDismissed,
    }),
    [
      deletionRequest,
      isLoading,
      cancelMutation.isPending,
      daysRemaining,
      cancelDeletion,
      refresh,
      dismissTemporarily,
      isDismissed,
    ],
  )

  return <DeletionWarningContext.Provider value={value}>{children}</DeletionWarningContext.Provider>
}

// ─────────────────────────────────────────────────────────────
// HOOK
// ─────────────────────────────────────────────────────────────

export const useDeletionWarning = (): DeletionWarningContextValue => {
  const context = useContext(DeletionWarningContext)
  if (!context) {
    throw new Error('useDeletionWarning must be used within DeletionWarningProvider')
  }
  return context
}
